if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.marqueeResizeHandler) {
        window.removeEventListener('resize', this.marqueeResizeHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || this.moveLocalization.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }

      // Rebuild the marquee whenever the viewport width changes, so it always
      // stays wide enough to loop without a visible gap.
      this.marqueeTrack = this.querySelector('.announcement__marquee-track');
      if (this.marqueeTrack) {
        let resizeTimer;
        this.marqueeResizeHandler = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.setupMarquee(), 200);
        };
        window.addEventListener('resize', this.marqueeResizeHandler);
      }

      // Announcement CTAs that point at the cart (e.g. "See Rewards") open the
      // cart drawer instead of navigating. Falls back to the /cart page if the
      // drawer isn't present or JS fails.
      this.addEventListener('click', (evt) => {
        const link = evt.target.closest('a');
        if (!link) return;
        const path = link.getAttribute('href') || '';
        if (!/(^|\/)cart\/?($|\?|#)/.test(path)) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          evt.preventDefault();
          drawer.open();
        }
      });
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }

      if (this.marqueeTrack) {
        this.setupMarquee();
      }
    }

    /**
     * Make the marquee loop seamlessly regardless of how many announcements
     * there are. The CSS animation slides the track by -50%, which only loops
     * without a gap if each half of the track is at least as wide as the bar.
     * We capture the original announcement set once, measure its width, and
     * repeat it enough times (an even number of copies) to overflow the bar,
     * scaling the animation duration so the scroll speed stays constant.
     */
    setupMarquee() {
      const track = this.marqueeTrack;
      if (!track) return;

      // Capture the unique announcement set once (Liquid renders it twice, so
      // the first half is the unique set) and its base per-copy duration.
      if (!this.marqueeBase) {
        const kids = Array.from(track.children);
        if (!kids.length) return;
        const half = Math.ceil(kids.length / 2);
        this.marqueeBase = kids.slice(0, half).map((node) => node.cloneNode(true));
        this.marqueeBaseDuration = parseFloat(getComputedStyle(track).animationDuration) || 40;
      }
      const base = this.marqueeBase;
      if (!base.length) return;

      // Render one set to measure its width.
      track.replaceChildren(...base.map((node) => node.cloneNode(true)));
      const setWidth = track.scrollWidth;
      const barWidth = this.clientWidth || setWidth;
      if (!setWidth) {
        // Not laid out yet — try again next frame.
        requestAnimationFrame(() => this.setupMarquee());
        return;
      }

      // Even number of copies; each half spans at least the full bar width.
      const copies = Math.max(2, 2 * Math.ceil(barWidth / setWidth));
      const frag = document.createDocumentFragment();
      for (let c = 0; c < copies; c += 1) {
        base.forEach((node) => {
          const clone = node.cloneNode(true);
          if (c > 0) clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        });
      }
      track.replaceChildren(frag);
      track.style.animationDuration = `${this.marqueeBaseDuration * (copies / 2)}s`;
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
